import type { CSSProperties, FC, ReactNode } from 'react';

import * as styles from './section-layout.css';

function joinClasses(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

interface SectionStackProps {
  children: ReactNode;
  tight?: boolean;
  className?: string;
}

export const SectionStack: FC<SectionStackProps> = ({
  children,
  tight = false,
  className,
}) => (
  <div
    className={joinClasses(styles.stack, tight && styles.stackTight, className)}
  >
    {children}
  </div>
);

interface SectionControlRowProps {
  label: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  coachmarkTarget?: string;
}

export const SectionControlRow: FC<SectionControlRowProps> = ({
  label,
  description,
  children,
  coachmarkTarget,
}) => (
  <div className={styles.controlRow} data-coachmark-target={coachmarkTarget}>
    <div className={styles.controlInfo}>
      <span className={styles.controlLabel}>{label}</span>
      {description && (
        <span className={styles.controlDescription}>{description}</span>
      )}
    </div>
    <div className={styles.controlSlot}>{children}</div>
  </div>
);

interface SectionPanelProps {
  children: ReactNode;
  className?: string;
  'data-section'?: string;
}

export const SectionPanel: FC<SectionPanelProps> = ({
  children,
  className,
  'data-section': dataSection,
}) => (
  <div
    className={joinClasses(styles.panel, className)}
    data-section={dataSection}
  >
    {children}
  </div>
);

interface SectionPanelHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

export const SectionPanelHeader: FC<SectionPanelHeaderProps> = ({
  title,
  description,
  action,
}) => (
  <div className={styles.panelHeader}>
    <div className={styles.panelCopy}>
      <span className={styles.panelTitle}>{title}</span>
      {description && (
        <span className={styles.panelDescription}>{description}</span>
      )}
    </div>
    {action}
  </div>
);

export const SectionPanelHint: FC<{ children: ReactNode }> = ({ children }) => (
  <span className={styles.panelHint}>{children}</span>
);

export const SectionEmpty: FC<{ children: ReactNode }> = ({ children }) => (
  <div className={styles.empty}>{children}</div>
);

interface SectionNoticeProps {
  children: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  variant?: 'info' | 'warning' | 'system';
}

export const SectionNotice: FC<SectionNoticeProps> = ({
  children,
  icon,
  action,
  variant = 'info',
}) => (
  <div className={styles.notice} data-variant={variant}>
    {icon && <span className={styles.noticeIcon}>{icon}</span>}
    <div className={styles.noticeContent}>{children}</div>
    {action}
  </div>
);

interface SectionTableRowProps {
  children: ReactNode;
  action?: ReactNode;
  indented?: boolean;
  spaced?: boolean;
}

export const SectionTableRow: FC<SectionTableRowProps> = ({
  children,
  action,
  indented = false,
  spaced = false,
}) => (
  <div
    className={styles.tableRow}
    data-indented={indented || undefined}
    data-spaced={spaced || undefined}
  >
    <div
      className={joinClasses(
        styles.tableMain,
        indented && styles.tableMainInline
      )}
    >
      {children}
    </div>
    {action && <div className={styles.tableAction}>{action}</div>}
  </div>
);

export const SectionRemoveButton: FC<{
  children?: ReactNode;
  onClick: () => void;
  title?: string;
}> = ({ children = '×', onClick, title }) => (
  <button className={styles.removeButton} onClick={onClick} title={title}>
    {children}
  </button>
);

export const SectionAddLink: FC<{
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ children, onClick, disabled = false }) => (
  <button className={styles.addLink} disabled={disabled} onClick={onClick}>
    {children}
  </button>
);

export const SectionDivider: FC = () => <div className={styles.divider} />;

interface SectionMappingGridProps {
  children: ReactNode;
  columns: string;
}

export const SectionMappingHeader: FC<SectionMappingGridProps> = ({
  children,
  columns,
}) => (
  <div
    className={styles.mappingHeader}
    style={{ gridTemplateColumns: columns } as CSSProperties}
  >
    {children}
  </div>
);

export const SectionMappingRow: FC<SectionMappingGridProps> = ({
  children,
  columns,
}) => (
  <div
    className={styles.mappingRow}
    data-streamlain-mapping-row
    style={{ gridTemplateColumns: columns } as CSSProperties}
  >
    {children}
  </div>
);

export const SectionSearchBox: FC<{
  icon?: ReactNode;
  children: ReactNode;
}> = ({ icon, children }) => (
  <div className={styles.searchBox}>
    {icon}
    {children}
  </div>
);

export const sectionSearchInputClassName = styles.searchInput;
